import React, { useMemo } from "react";
import { Modal } from "react-bootstrap";
import "../css/orders-insight.css";

/* type: "rating" — хэрэглэгчийн үнэлгээ | "speed" — 5 минутын гүйцэтгэл */
export default function RatingDetailModal({
  show,
  handleClose,
  week,
  type = "speed",
}) {
  const days = useMemo(() => week?.days || [], [week]);
  const isRating = type === "rating";

  const maxTotal = useMemo(
    () => Math.max(1, ...days.map((d) => d.total)),
    [days]
  );

  const summary = useMemo(() => {
    const orders = days.reduce((sum, d) => sum + d.total, 0);
    const bad = days.reduce((sum, d) => sum + d.bad, 0);
    const reviews = days.reduce((sum, d) => sum + (d.reviews || 0), 0);
    const ratingSum = days.reduce(
      (sum, d) => sum + (d.rating || 0) * (d.reviews || 0),
      0
    );

    return {
      orders,
      bad,
      reviews,
      onTimePercent: orders ? Math.round(((orders - bad) / orders) * 100) : 0,
      avgRating: reviews ? ratingSum / reviews : 0,
    };
  }, [days]);

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isRating
            ? "Хэрэглэгчийн үнэлгээ — өдрөөр"
            : "5 минутын гүйцэтгэл — өдрөөр"}
          {week?.title && (
            <div className="modal-subtitle">
              {week.title} · {week.range}
            </div>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="px-4 pt-3 pb-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          {isRating ? (
            <div className="text-muted">
              Нийт <b className="text-body">{summary.reviews}</b> үнэлгээний
              дундаж{" "}
              <b className="text-body">{summary.avgRating.toFixed(1)}</b>
              <i className="mdi mdi-star text-warning ms-1" />
            </div>
          ) : (
            <>
              <div className="text-muted">
                Нийт <b className="text-body">{summary.orders}</b> захиалгаас{" "}
                <b className="text-danger">{summary.bad}</b> нь 5 минутаас
                хоцорсон
                <span className="ms-2 badge bg-success-subtle text-success">
                  {summary.onTimePercent}%
                </span>
              </div>

              <div className="daily-legend">
                <span className="daily-legend__item">
                  <span className="daily-legend__dot daily-legend__dot--good" />
                  Сайн
                </span>
                <span className="daily-legend__item">
                  <span className="daily-legend__dot daily-legend__dot--bad" />
                  Муу
                </span>
              </div>
            </>
          )}
        </div>

        {/* График */}
        <div className="daily-chart mb-4">
          {days.map((day) => {
            const good = day.total - day.bad;

            return (
              <div className="daily-chart__col" key={day.key}>
                <span className="daily-chart__total">
                  {isRating ? (day.rating || 0).toFixed(1) : day.total}
                </span>

                <div className="daily-chart__track">
                  <div
                    className="daily-chart__fill"
                    style={{
                      height: isRating
                        ? `${((day.rating || 0) / 5) * 100}%`
                        : `${(day.total / maxTotal) * 100}%`,
                    }}
                  >
                    {isRating ? (
                      <div className="daily-chart__rating" />
                    ) : (
                      <>
                        {day.bad > 0 && (
                          <div
                            className="daily-chart__bad"
                            style={{
                              height: `${(day.bad / day.total) * 100}%`,
                            }}
                            title={`${day.bad} муу`}
                          />
                        )}
                        <div
                          className="daily-chart__good"
                          style={{ height: `${(good / day.total) * 100}%` }}
                          title={`${good} сайн`}
                        />
                      </>
                    )}
                  </div>
                </div>

                <span className="daily-chart__label">
                  {day.shortLabel || day.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Хүснэгт */}
        <div className="table-responsive">
          <table className="table table-sm table-centered mb-0">
            <thead>
              {isRating ? (
                <tr>
                  <th>Өдөр</th>
                  <th className="text-center">Үнэлгээ</th>
                  <th className="text-center">Үнэлсэн тоо</th>
                  <th className="text-center">Захиалга</th>
                </tr>
              ) : (
                <tr>
                  <th>Өдөр</th>
                  <th className="text-center">Нийт</th>
                  <th className="text-center">Сайн</th>
                  <th className="text-center">Муу</th>
                  <th className="text-center">Хувь</th>
                </tr>
              )}
            </thead>

            <tbody>
              {days.map((day) => {
                const good = day.total - day.bad;
                const percent = day.total
                  ? Math.round((good / day.total) * 100)
                  : 0;

                return (
                  <tr key={day.key}>
                    <td>
                      {day.label} <span className="text-muted">{day.date}</span>
                    </td>

                    {isRating ? (
                      <>
                        <td className="text-center">
                          {(day.rating || 0).toFixed(1)}
                          <i className="mdi mdi-star text-warning ms-1" />
                        </td>
                        <td className="text-center">{day.reviews || 0}</td>
                        <td className="text-center text-muted">{day.total}</td>
                      </>
                    ) : (
                      <>
                        <td className="text-center">{day.total}</td>
                        <td className="text-center text-success">{good}</td>
                        <td className="text-center text-danger">
                          {day.bad || "-"}
                        </td>
                        <td className="text-center">{percent}%</td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Modal.Body>
    </Modal>
  );
}
